import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Breadcrumb, BackdropCircular } from 'components';
import {
  Card,
  Badge,
  Button,
  Tab,
  Tabs
} from '@material-ui/core';
import { Formik } from 'formik';
import {
  getHomepageCTABlockConfigsApi,
  saveHomepageCTABlockConfigsApi,
  getStoreCategoriesApi,
  getAssetManagerCategoryTagsApi
} from 'api';
import CTAGeneral from './CTAGeneral';
import CTABlockForm from './CTABlockForm';
import CTAPreview from './CTAPreview';


const ctaTabs: string[] = [
  'General',
  'CTA Top Left Block',
  'CTA Top Right Block',
  'CTA Bottom Block'
];

const CTABlock: React.FC = () => {
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState<number>(0);

  const {
    homepageCTABlockConfigs,
    loadingHomepageCTABlockConfigs,
    homepageCTABlockConfigsToUpdate,
    savingHomepageCTABlockConfigs
  } = useSelector((state: IReduxState) => state.store_configs);

  const { storeActiveCategories } = useSelector((state: IReduxState) => state.categories);

  const {
    assetCategoryTags,
    loadingAssetCategoryTags
  } = useSelector((state: IReduxState) => state.asset_manager);

  const loading: boolean = loadingHomepageCTABlockConfigs
    || savingHomepageCTABlockConfigs
    || loadingAssetCategoryTags;

  useEffect(() => {
    if (!Object.keys(homepageCTABlockConfigs['general']).length) {
      getHomepageCTABlockConfigsApi({ dispatch });
    }

    if (!storeActiveCategories.length) {
      getStoreCategoriesApi({ dispatch, checkStoreType: false });
    }

    if (!assetCategoryTags.length) {
      getAssetManagerCategoryTagsApi({ dispatch, categoryName: 'Storebuild-automaton' });
    }
  }, [dispatch, homepageCTABlockConfigs, storeActiveCategories, assetCategoryTags]);

  const handleTabChange = (_event: React.SyntheticEvent, value: number): void => {
    setTabIndex(value);
  };

  const setTabLabel = (tabName: string, errors: FormikErrors): React.ReactNode => {
    if (tabName === ctaTabs[1] && errors?.horizontal_top) {
      return (
        <Badge badgeContent="!" color="error">
          {tabName}
        </Badge>
      );
    }

    if (tabName === ctaTabs[2] && errors?.horizontal_bottom) {
      return (
        <Badge badgeContent="!" color="error">
          {tabName}
        </Badge>
      );
    }

    if (tabName === ctaTabs[3] && errors?.vertical) {
      return (
        <Badge badgeContent="!" color="error">
          {tabName}
        </Badge>
      );
    }

    return tabName;
  };

  const handleSave = (): void => {
    saveHomepageCTABlockConfigsApi({
      dispatch,
      configs: homepageCTABlockConfigsToUpdate
    });
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <Breadcrumb
        routeSegments={[
          { name: 'Settings', path: '/settings#go_live_requirements' },
          { name: 'UI Configurations', path: '/settings#UI_configurations' },
          { name: 'Homepage Settings', path: '/configurations/homepage' },
          { name: 'Call to Actions' }
        ]}
      />
      <div className="m-10">
        {!!Object.keys(homepageCTABlockConfigs['general']).length && (
          <Formik
            onSubmit={handleSave}
            initialValues={{ ...homepageCTABlockConfigs }}
          >
            {({ values, errors, setFieldValue, handleSubmit }) => (
              <form autoComplete="off" onSubmit={handleSubmit}>
                <Card className="p-8">
                  <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    {ctaTabs.map((tab, idx) => (
                      <Tab
                        className="capitalize font-medium px-8"
                        key={`tab-${idx}`}
                        value={idx}
                        label={setTabLabel(tab, errors)}
                      />
                    ))}
                  </Tabs>
                  {tabIndex === 0 && (
                    <CTAGeneral
                      values={values}
                      setFieldValue={setFieldValue}
                    />
                  )}
                  {tabIndex === 1 && (
                    <CTABlockForm
                      blockType="horizontal_top"
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                    />
                  )}
                  {tabIndex === 2 && (
                    <CTABlockForm
                      blockType="horizontal_bottom"
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                    />
                  )}
                  {tabIndex === 3 && (
                    <CTABlockForm
                      blockType="vertical"
                      values={values}
                      errors={errors}
                      setFieldValue={setFieldValue}
                    />
                  )}
                </Card>
                <CTAPreview values={homepageCTABlockConfigs} />
                <div className="flex justify-end">
                  <Button
                    variant="contained"
                    color="primary"
                    className="my-10"
                    onClick={handleSave}
                  >
                    Save Configurations
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        )}
      </div>
      <BackdropCircular loading={loading} />
    </div>
  );
};

export default CTABlock;
